import {
	Helpers,
	RuleEngineEntities,
	RuleEngineLegacyEntities
} from '@bimser/components/dist/BCRuleManager';
import { IDictionary, isNullOrUndefined, lazyRetry, RxEventEmitter } from '@bimser/core';
import { ConditionTester, convertConditions, getControlPropertyValue } from '.';
import { ICheckRules } from '../../../entities/ICheckRules';
import { FormItemManagerClass } from '../../../environments/runtime/FormItemManager';
import { runShowConfirmationAction } from '../action';
import { triggerAction } from './triggerAction';
import { checkLegacyRules } from './checkLegacyRules';
import { Map } from 'immutable';

export function* checkFormItemRules(args: ICheckRules) {
	let result: boolean = false;

	if (!args.formData) {
		return result;
	}

	// "getControlPropertyValue" methodunda preview modunda ihtiyaç olduğu için bundle a dahil etmemek için burda oluşturuluyor.
	const FormRuntimeItemManager: FormItemManagerClass =
		args.checkType === 'preview'
			? yield lazyRetry(
					() =>
						import(
							/* webpackChunkName: "web-form-viewer" */ '../../../environments/runtime/FormItemManager'
						)
			  ).then(({ default: FormRuntimeItemManager }) => FormRuntimeItemManager)
			: null;

	if (args.rules.count()) {
		const getFactValue = (factKey: string) => {
			const controlName = factKey.split('.')[0];
			const propKey = factKey
				.split('.')
				.splice(1, factKey.split('.').length + 1)
				.join('.');

			const fact = getControlPropertyValue({
				controlId: controlName,
				keys: propKey,
				data: args.formData,
				checkType: args.checkType,
				FormRuntimeItemManager
			});

			return isNullOrUndefined(fact) ? null : fact;
		};

		const sortedRules = args.rules
			.sort((a, b) => {
				if (a.orderNo < b.orderNo) {
					return -1;
				} else if (a.orderNo > b.orderNo) {
					return 1;
				} else {
					return 0;
				}
			})
			.toJS() as IDictionary<RuleEngineEntities.IRule>;

		for (const ruleId in sortedRules) {
			const rule = sortedRules[ruleId];

			if (rule.enabled) {
				if (Helpers.isLegacyRule(rule)) {
					const stopProcessFlag = yield checkLegacyRules({
						checkType: 'execution',
						formData: args.formData,
						rule: rule,
						rules: args.rules as Map<string, RuleEngineLegacyEntities.Rule>,
						windowId: args.windowId,
						validate: args.validate
					});

					if (stopProcessFlag) {
						result = true;
					}
				} else {
					for (const flowId in rule.ruleFlows) {
						const flow = rule.ruleFlows[flowId];
						const stopProcessFlag = yield runFlow(rule, flow, getFactValue, {
							...args,
							rule: rule
						} as ICheckRules<RuleEngineEntities.IRule, RuleEngineEntities.Rule>);

						if (stopProcessFlag) {
							result = true;
						}
					}
				}
			}
		}
	}

	if (
		RxEventEmitter.subjects[`$${args.windowId}`] ||
		RxEventEmitter.subjects[`$checkRules_${args.windowId}`]
	) {
		RxEventEmitter.emit('checkRules_' + args.windowId, {
			type: 'CHECK_RULES_RESULT',
			windowId: args.windowId,
			result: result
		});
	}

	return result;
}

function* runFlow(
	rule: RuleEngineEntities.IRule,
	flow: RuleEngineEntities.IRuleFlow,
	getFactValue: (factKey: string) => any,
	args: ICheckRules<RuleEngineEntities.IRule, RuleEngineEntities.Rule>
) {
	let result: boolean = false;
	switch (flow.typeName) {
		case 'ActionGroup': {
			const actionGroupId = flow.referenceId;
			const actionKeys = rule.actionGroups[actionGroupId];
			let actions: Array<RuleEngineEntities.IAction> = [];
			actionKeys.forEach(actionKey => {
				actions.push(rule.actions[actionKey]);
			});

			result = yield triggerAction(actions, args);
			break;
		}
		case 'Condition': {
			const conditionId = flow.referenceId;
			const condition = rule.conditions[conditionId];

			if (condition.typeName === 'ConfirmationCondition') {
				yield runShowConfirmationAction(condition, {
					checkType: args.checkType,
					rule,
					windowId: args.windowId
				});
			} else {
				const convertedCondition = convertConditions(condition, args.formData);

				const success = ConditionTester.compare(convertedCondition, {
					[convertedCondition.fact]: getFactValue(convertedCondition.fact)
				});

				if (success && condition.onSuccess) {
					result = yield runFlow(rule, condition.onSuccess, getFactValue, args);
				} else if (!success && condition.onFailure) {
					result = yield runFlow(rule, condition.onFailure, getFactValue, args);
				}
			}

			break;
		}
	}

	return result;
}
